import React, { useState } from 'react';
import { ProjectCard } from '../components/ProjectCard';
import { useData } from '../contexts/DataContext';
import { PROJECT_STAGES } from '../constants/stages';

export const Portfolio: React.FC = () => {
  const { projects, loading } = useData();
  const [filter, setFilter] = useState<string>('All');

  // Filter pills come from the single canonical stage list (constants/stages.ts),
  // plus any off-list stage a real project already uses so nothing gets hidden.
  const dynamicStages = projects.map(p => p.stage).filter((s): s is string => Boolean(s));
  const stages = Array.from(new Set(['All', ...PROJECT_STAGES, ...dynamicStages]));

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => (p.stage || 'Concept') === filter);

  return (
    <div className="pt-36 md:pt-44 pb-24 min-h-screen container mx-auto px-4 md:px-6">
      {/* Header lives OUTSIDE the loading gate: the h1 + intro are in the DOM
          (and in any crawler snapshot) even while projects are still loading.
          The venture count waits for the data so it never flashes "0". */}
      <header className="mb-10 md:mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-[11px] md:text-xs font-semibold uppercase tracking-[0.25em] text-zinc-400 dark:text-zinc-500 [.nardogrey_&]:text-zinc-300">
            Portfolio
          </span>{' '}
          {/* ^ real whitespace between the two spans: CSS-blind extractors
              would otherwise read "Portfolio10 Ventures". Whitespace-only text
              is never rendered as a flex item, so layout is untouched. */}
          {!loading && (
            <span className="text-[11px] md:text-xs font-medium tabular-nums uppercase tracking-[0.2em] text-zinc-400 dark:text-zinc-600 [.nardogrey_&]:text-zinc-400">
              {projects.length} {projects.length === 1 ? 'Venture' : 'Ventures'}
            </span>
          )}
        </div>
        <h1 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-zinc-900 dark:text-white/90 [.nardogrey_&]:text-white">
          Venture Portfolio
        </h1>
        <p className="mt-4 md:mt-5 max-w-2xl text-sm md:text-base leading-relaxed text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-300">
          The venture portfolio of Ruhlan Jafarli, known online as helenendorfsky.
          Flagship ventures include Sevenoir, Wealthernal, Oraculore, and Sycloop,
          spanning AI, blockchain, fintech, and digital identity. Every stage is
          represented here, from first concept to exit.
        </p>
      </header>

      {loading ? (
        <div className="flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-500"></div>
        </div>
      ) : (
        <>
          {/* Filter pills as a real <ul> so CSS-blind text extraction keeps a
              word boundary between adjacent stage names. */}
          <ul className="flex flex-wrap gap-2 md:gap-3 mb-10 md:mb-16 list-none m-0 p-0 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {stages.map(stage => (
              <li key={stage}>
                <button
                  onClick={() => setFilter(stage)}
                  className={`
                    px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 border
                    ${filter === stage
                      ? `bg-zinc-900 text-white border-zinc-900 shadow-lg dark:bg-white dark:text-black dark:border-white [.nardogrey_&]:bg-white [.nardogrey_&]:text-zinc-900 [.nardogrey_&]:border-white [.nardogrey_&]:shadow-none`
                      : `bg-transparent text-zinc-500 border-zinc-300 hover:border-zinc-500 hover:text-zinc-900 dark:border-zinc-800 dark:hover:border-zinc-600 dark:hover:text-zinc-300 [.nardogrey_&]:border-white/20 [.nardogrey_&]:text-zinc-400 [.nardogrey_&]:hover:border-white/50 [.nardogrey_&]:hover:text-white`}
                  `}
                >
                  {stage}
                </button>
              </li>
            ))}
          </ul>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {filteredProjects.map((project, index) => (
              // Entrance animation lives on the wrapper so it never fights the card's
              // own hover -translate-y (both would target `transform` on one element,
              // and fill-mode-both would pin it, killing the lift).
              <div
                key={project.id}
                className="h-full animate-in fade-in slide-in-from-bottom-12 duration-700 fill-mode-both"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <ProjectCard project={project} wide />
              </div>
            ))}
          </div>

          {filteredProjects.length === 0 && (
             <div className="text-center py-20 text-sm md:text-base text-zinc-500 dark:text-zinc-400 [.nardogrey_&]:text-zinc-400 animate-in fade-in duration-500">
               {filter === 'All' ? (
                 <p>No ventures to show yet.</p>
               ) : (
                 <p>
                   No ventures at the{' '}
                   <span className="font-medium text-zinc-700 dark:text-zinc-200 [.nardogrey_&]:text-white">{filter}</span>{' '}
                   stage yet.
                 </p>
               )}
             </div>
          )}
        </>
      )}
    </div>
  );
};
